import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import {
  deviceApi,
  getDeviceInfo,
  setStoredDeviceToken,
  type RegisteredDevice,
  type DeviceRegistrationCode,
} from '@/api/device'
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Monitor,
  QrCode,
  Copy,
  Check,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'

export default function DeviceManagement() {
  const { t, i18n } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()

  // State
  const [searchTerm, setSearchTerm] = useState('')
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false)
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingDevice, setEditingDevice] = useState<RegisteredDevice | null>(null)
  const [deviceName, setDeviceName] = useState('')
  const [editDeviceName, setEditDeviceName] = useState('')
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  // Queries
  const { data: devicesData, isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: () => deviceApi.getAll(),
  })

  const { data: codesData } = useQuery({
    queryKey: ['device-codes'],
    queryFn: () => deviceApi.getCodes(),
  })

  const devices = devicesData?.data?.data || []
  const codes = codesData?.data?.data || []

  // Mutations
  const registerMutation = useMutation({
    mutationFn: (data: { deviceName: string; deviceInfo?: Record<string, unknown> }) =>
      deviceApi.register(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      const device = response.data.data
      setStoredDeviceToken(device.deviceToken)
      alert(t('admin:device.toast.registerSuccess', { name: device.deviceName }))
      closeRegisterModal()
    },
    onError: (error: any) => {
      alert(t('admin:device.toast.errorPrefix', {
        message: error?.response?.data?.error?.message || t('admin:device.toast.registerFailedDefault'),
      }))
    },
  })

  const createCodeMutation = useMutation({
    mutationFn: () => deviceApi.createCode(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-codes'] })
    },
    onError: (error: any) => {
      alert(t('admin:device.toast.errorPrefix', {
        message: error?.response?.data?.error?.message || t('admin:device.toast.codeCreateFailedDefault'),
      }))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { deviceName?: string; isActive?: boolean } }) =>
      deviceApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      closeEditModal()
    },
    onError: (error: any) => {
      alert(t('admin:device.toast.errorPrefix', {
        message: error?.response?.data?.error?.message || t('admin:device.toast.updateFailedDefault'),
      }))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deviceApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (error: any) => {
      alert(t('admin:device.toast.errorPrefix', {
        message: error?.response?.data?.error?.message || t('admin:device.toast.deleteFailedDefault'),
      }))
    },
  })

  // Filtered devices
  const filteredDevices = devices.filter((d: RegisteredDevice) =>
    searchTerm === '' ||
    d.deviceName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Active registration codes (not used, not expired)
  const activeCodes = codes.filter((c: DeviceRegistrationCode) => !c.isUsed && !c.isExpired)

  // Handlers
  const closeRegisterModal = () => {
    setIsRegisterModalOpen(false)
    setDeviceName('')
  }

  const closeEditModal = () => {
    setIsEditModalOpen(false)
    setEditingDevice(null)
    setEditDeviceName('')
  }

  const handleRegisterCurrentDevice = (e: React.FormEvent) => {
    e.preventDefault()
    registerMutation.mutate({
      deviceName,
      deviceInfo: getDeviceInfo(),
    })
  }

  const handleCreateCode = () => {
    createCodeMutation.mutate()
  }

  const getActivateUrl = (code: string) => {
    return `${window.location.origin}/device/activate?code=${code}`
  }

  const handleEdit = (device: RegisteredDevice) => {
    setEditingDevice(device)
    setEditDeviceName(device.deviceName)
    setIsEditModalOpen(true)
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingDevice) return
    updateMutation.mutate({
      id: editingDevice.id,
      data: { deviceName: editDeviceName },
    })
  }

  const handleToggleActive = (device: RegisteredDevice) => {
    updateMutation.mutate({
      id: device.id,
      data: { isActive: !device.isActive },
    })
  }

  const handleDelete = (device: RegisteredDevice) => {
    if (confirm(t('admin:device.confirm.delete', { name: device.deviceName }))) {
      deleteMutation.mutate(device.id)
    }
  }

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin:device.title')}</h1>
          <p className="mt-1 text-gray-500">
            {t('admin:device.subtitle')}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsCodeModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
          >
            <QrCode className="w-5 h-5" />
            <span>{t('admin:device.actions.codeButton')}</span>
          </button>
          <button
            onClick={() => setIsRegisterModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5" />
            <span>{t('admin:device.actions.registerButton')}</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder={t('admin:device.search.placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Device List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('common:label.loading')}</div>
        ) : filteredDevices.length === 0 ? (
          <div className="p-12 text-center">
            <Monitor className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('admin:device.empty.title')}</p>
            <p className="text-sm text-gray-400 mt-1">
              {t('admin:device.empty.subtitle')}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin:device.table.deviceName')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin:device.table.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin:device.table.lastAccess')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin:device.table.registeredAt')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('admin:device.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredDevices.map((device: RegisteredDevice) => (
                <tr key={device.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Monitor className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="font-medium text-gray-900">{device.deviceName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(device)}
                      className="flex items-center space-x-2"
                      title={device.isActive ? t('admin:device.actions.deactivate') : t('admin:device.actions.activate')}
                    >
                      {device.isActive ? (
                        <>
                          <ToggleRight className="w-6 h-6 text-green-500" />
                          <span className="text-sm text-green-700">{t('admin:device.status.active')}</span>
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-6 h-6 text-gray-400" />
                          <span className="text-sm text-gray-500">{t('admin:device.status.inactive')}</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(device.lastAccessedAt)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(device.createdAt)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(device)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        title={t('admin:device.actions.editName')}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(device)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title={t('common:button.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Register Current Device Modal */}
      <Dialog open={isRegisterModalOpen} onOpenChange={(open) => { if (!open) closeRegisterModal() }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin:device.modal.register.title')}</DialogTitle>
          </DialogHeader>
          <form id="register-form" onSubmit={handleRegisterCurrentDevice} className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('admin:device.modal.register.description')}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin:device.modal.register.deviceNameLabel')}
              </label>
              <input
                type="text"
                required
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('admin:device.modal.register.deviceNamePlaceholder')}
              />
            </div>
          </form>
          <DialogFooter>
            <button
              type="button"
              onClick={closeRegisterModal}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              form="register-form"
              disabled={registerMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {t('admin:device.modal.register.submitButton')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registration Code Modal */}
      <Dialog open={isCodeModalOpen} onOpenChange={setIsCodeModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('admin:device.modal.code.title')}</DialogTitle>
          </DialogHeader>
        <div className="space-y-6">
          {/* Create Code */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {t('admin:device.modal.code.description')}
              <br />
              <span className="text-gray-400">{t('admin:device.modal.code.validityNote')}</span>
            </p>
            <button
              onClick={handleCreateCode}
              disabled={createCodeMutation.isPending}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap ml-4"
            >
              <Plus className="w-4 h-4" />
              <span>{createCodeMutation.isPending ? t('admin:device.modal.code.generating') : t('admin:device.modal.code.createButton')}</span>
            </button>
          </div>

          {/* Active Codes List */}
          {activeCodes.length > 0 && (
            <div className="space-y-3">
              {activeCodes.map((codeItem: DeviceRegistrationCode) => (
                <div
                  key={codeItem.id}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <span className="font-mono text-2xl font-bold text-primary-600 tracking-widest">
                          {codeItem.code}
                        </span>
                        <button
                          onClick={() => handleCopyCode(codeItem.code)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                          title={t('admin:device.modal.code.copyCode')}
                        >
                          {copiedCode === codeItem.code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleCopyCode(getActivateUrl(codeItem.code))}
                          className="text-xs text-gray-400 hover:text-gray-600 hover:underline"
                          title={t('admin:device.modal.code.copyUrl')}
                        >
                          {copiedCode === getActivateUrl(codeItem.code) ? t('admin:device.modal.code.copied') : t('admin:device.modal.code.copyUrl')}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        {codeItem.deviceName} &middot; {t('admin:device.modal.code.expireLabel', { date: formatDate(codeItem.expiresAt) })}
                      </p>
                    </div>
                    <div className="ml-4 p-2 bg-white rounded-lg border border-gray-200">
                      <QRCodeSVG
                        value={getActivateUrl(codeItem.code)}
                        size={96}
                        level="M"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeCodes.length === 0 && (
            <div className="text-center py-6 text-gray-400">
              <QrCode className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('admin:device.modal.code.noCodesEmpty')}</p>
            </div>
          )}
        </div>
        </DialogContent>
      </Dialog>

      {/* Edit Device Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => { if (!open) closeEditModal() }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin:device.modal.edit.title')}</DialogTitle>
          </DialogHeader>
          <form id="edit-form" onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin:device.modal.edit.deviceNameLabel')}
              </label>
              <input
                type="text"
                required
                value={editDeviceName}
                onChange={(e) => setEditDeviceName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </form>
          <DialogFooter>
            <button
              type="button"
              onClick={closeEditModal}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              form="edit-form"
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {t('admin:device.modal.edit.submitButton')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
